// ABOUTME: Campaign enrollments tab for campaign detail page
// ABOUTME: Shows all enrollments with filters and unenroll functionality

import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import {
  CampaignEnrollment,
  CampaignEnrollmentsResponse,
  DeleteEnrollmentResponse
} from '../../types/emailCampaign';
import { getCurrentAccessToken } from '../../services/api';

interface CampaignEnrollmentsTabProps {
  campaignId: number;
  campaignName: string;
}

export default function CampaignEnrollmentsTab({
  campaignId,
  campaignName
}: CampaignEnrollmentsTabProps) {
  const [enrollments, setEnrollments] = useState<CampaignEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enrollmentToDelete, setEnrollmentToDelete] = useState<CampaignEnrollment | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEnrollments();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchEnrollments is a plain function in component scope; the effect correctly re-runs when the listed deps change, not when the function reference changes
  }, [page, pageSize, statusFilter, searchQuery]);

  const fetchEnrollments = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getCurrentAccessToken();
      const response = await axios.get<CampaignEnrollmentsResponse>(
        `${import.meta.env.VITE_API_URL}/api/admin/email-campaigns/${campaignId}/enrollments`,
        {
          params: {
            page: page + 1,
            pageSize,
            status: statusFilter,
            search: searchQuery || undefined
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setEnrollments(response.data.data);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
      console.error('Error fetching enrollments:', err);
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Failed to fetch enrollments');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (enrollment: CampaignEnrollment) => {
    setEnrollmentToDelete(enrollment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!enrollmentToDelete) return;

    setDeleting(true);
    setError(null);
    try {
      const token = getCurrentAccessToken();
      const response = await axios.delete<DeleteEnrollmentResponse>(
        `${import.meta.env.VITE_API_URL}/api/admin/email-campaigns/enrollments/${enrollmentToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(`${response.data.message}. Cancelled ${response.data.cancelledEmailsCount} pending email(s).`);
      setDeleteDialogOpen(false);
      setEnrollmentToDelete(null);
      fetchEnrollments(); // Refresh list
    } catch (err: unknown) {
      console.error('Error deleting enrollment:', err);
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Failed to remove enrollment');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEnrollmentToDelete(null);
  };

  const getStatusColor = (status: string): 'success' | 'info' | 'error' | 'default' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && enrollments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          label="Search families"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {enrollments.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No enrollments found
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Family</TableCell>
                  <TableCell>Enrolled</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Emails</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrollments.map((enrollment) => (
                  <TableRow key={enrollment.id}>
                    <TableCell>{enrollment.familyName}</TableCell>
                    <TableCell>
                      {new Date(enrollment.enrolledAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={enrollment.status}
                        size="small"
                        color={getStatusColor(enrollment.status)}
                      />
                    </TableCell>
                    <TableCell>
                      Step {enrollment.currentStepNumber} of {enrollment.totalSteps}
                    </TableCell>
                    <TableCell>
                      {enrollment.emailsSent} / {enrollment.totalScheduledEmails}
                    </TableCell>
                    <TableCell>{enrollment.triggerReason || '-'}</TableCell>
                    <TableCell align="right">
                      {enrollment.status.toLowerCase() !== 'completed' && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(enrollment)}
                          title="Remove enrollment"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Remove Enrollment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{enrollmentToDelete?.familyName}</strong> from{' '}
            <strong>{campaignName}</strong>?
            <br />
            <br />
            This will cancel any pending scheduled emails for this enrollment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
